import React, { useEffect, useMemo, useRef } from "react";
import { RouteComponentProps } from "react-router-dom";

import {
  CurrencyFormatters,
  TravelWalletSelectionWorkflow,
  TravelWalletRestrictedCreditSelection,
  filterOutSpendAndGetOffers,
  getRestrictedATCText,
  getRestrictedUserTCTexts,
  isSpendAndGetOffer,
  getCreditActivityGroups,
  formatPartialHistoryValidFromDate,
  getUpcomingCreditActivityItems,
  ActionLink,
} from "halifax";
import { CaponeEmbeddedProvider, CreditActivityDialog } from "@capone/portal";
import "@capone/portal/style.css";
import { CreditActivityDialog as VesperCreditActivityDialog } from "@vesper/portal";
import { CreditActivityDialog as CorpCreditActivityDialog } from "@capone-corporate/portal";

import {
  ENGAGED_OFFER_CTA,
  RESTRICTED_TRAVEL_CREDIT_DISPLAYED,
  RESTRICTED_TRAVEL_CREDIT_TOGGLED,
  EDITED_RESTRICTED_CREDIT_AMOUNT,
  SelectedTravelOfferScreen,
  StatementCreditDetail,
  Tenant,
  TravelWalletCredit,
  CreditRestriction,
} from "redmond";
import {
  getConfigTenant,
  isCorpTenant,
  isVesperTenant,
  hasIneligibleTravelOffer,
} from "@capone/common";
import { ClientContext } from "../../../../App";
import { TravelWalletSelectionConnectorProps } from "./container";
import {
  AVAILABLE,
  C1_AUTO_SELECT_CARD_AT_CHECKOUT,
  CORP_FTB_OFFER_ADDITIONAL_PLACEMENTS,
  VESPER_PC_CREDIT,
  getExperimentVariant,
  useExperiments,
} from "../../../../context/experiments";
import {
  APPLY_TRAVEL_CREDIT_TITLE,
  APPLY_TRAVEL_OFFER_TITLE,
  CREDIT_BALANCE_DESCRIPTION,
  CREDIT_HISTORY_BALANCE_TEXT,
  CREDIT_HISTORY_MODAL_LIST_HEADER,
  CREDIT_HISTORY_MODAL_SUBHEADER,
  TRAVEL_CREDIT_AND_OFFER_TOTAL_PAYMENT_TEXT,
  TRAVEL_CREDIT_SUBTITLE,
  TRAVEL_CREDIT_TOTAL_PAYMENT_TEXT,
  TRAVEL_OFFER_SUBTITLE,
  TRAVEL_OFFER_TOTAL_PAYMENT_TEXT,
  VIEW_CREDIT_HISTORY_CTA,
  APPLY_VESPER_CREDIT_LABEL,
  getSelectedCreditDescription,
  getSelectedOfferDescription,
} from "./textConstants";
import { trackEvent } from "../../../../api/v0/analytics/trackEvent";

const DO_NOT_APPLY_REWARDS_KEY = "do-not-apply-rewards";

export interface ITravelWalletSelectionProps
  extends TravelWalletSelectionConnectorProps,
    RouteComponentProps {
  className?: string;
  isMobile?: boolean;
  disabled?: boolean;
  isAnnualTravelCreditsExperiment?: boolean;
  isCreditAndOfferStackingExperimentV1?: boolean;
  isTravelSale?: boolean;
}

const stringifyCreditRestrictions = (
  restrictions?: CreditRestriction[],
): string => {
  if (!restrictions?.length) return "";
  return restrictions
    .map((r) => {
      switch (r.RestrictionCriterion) {
        case "Product":
          return `Product:${[...r.allowedProducts].sort().join(",")}`;
        case "CollectionType":
          return `CollectionType:${[...r.allowedTypes].sort().join(",")}`;
        case "MinimumLengthOfStay":
          return `MinimumLengthOfStay:${r.minNights}`;
        default:
          return "";
      }
    })
    .join("|");
};

const getCreditActivityDialogByTenant = (tenant: Tenant) => {
  if (isCorpTenant(tenant)) return CorpCreditActivityDialog;
  if (isVesperTenant(tenant)) return VesperCreditActivityDialog;
  return CreditActivityDialog;
};

export const TravelWalletSelection = (props: ITravelWalletSelectionProps) => {
  const {
    offers,
    offerToApply,
    setOfferToApply,
    credits,
    creditBreakdown,
    creditsToApply,
    setCreditToApply,
    disabled,
    isTravelCreditPaymentOnly,
    isTravelWalletOfferPaymentOnly,
    isStackedTravelWalletPaymentOnly,
    isMobile,
    creditsHistoryTransaction,
    selectedPaymentMethodId,
    selectedRewardsPaymentAccountId,
    setSelectedRewardsPaymentTotal,
    resetPaymentCardSelectedAccounts,
    bestOverallOffer,
    rewardsAccounts,
    isAnnualTravelCreditsExperiment,
    isCreditAndOfferStackingExperimentV1,
    maxApplicableTravelWalletCredit,
    isTravelSale,
    ineligibleCreditsBreakdown,
    bookingTotal,
    allWalletOffers,
    currentFunnel,
    eligibleRestrictedPcCredits,
    appliedRestrictedCredits,
    maxApplicableRestrictedCreditAmounts,
    setRestrictedCreditAmountToApply,
    restrictedCreditCoversTotal,
    isBookingTotalCovered,
    selectedLodgingId,
    partialHistoryValidFromTime,
  } = props;

  const firstName =
    React.useContext(ClientContext).sessionInfo?.userInfo?.firstName || "";

  const filteredOffers = useMemo(
    () => (offers ? filterOutSpendAndGetOffers(offers) : undefined),
    [offers],
  );

  const filteredBestOverallOffer = useMemo(
    () =>
      bestOverallOffer && isSpendAndGetOffer(bestOverallOffer.descriptions)
        ? undefined
        : bestOverallOffer,
    [bestOverallOffer],
  );

  const expState = useExperiments();
  const isFtbEnabled =
    getExperimentVariant(
      expState.experiments,
      CORP_FTB_OFFER_ADDITIONAL_PLACEMENTS,
    ) === AVAILABLE;
  const autoSelectExperimentOn =
    getExperimentVariant(
      expState.experiments,
      C1_AUTO_SELECT_CARD_AT_CHECKOUT,
    ) === AVAILABLE;
  const isVesperPcCreditEnabled =
    getExperimentVariant(expState.experiments, VESPER_PC_CREDIT) === AVAILABLE;

  const hasIneligibleFTB = useMemo(
    () =>
      isFtbEnabled &&
      isCorpTenant(getConfigTenant()) &&
      hasIneligibleTravelOffer(allWalletOffers, filteredOffers, currentFunnel),
    [isFtbEnabled, filteredOffers, allWalletOffers, currentFunnel],
  );

  // Carve all eligible restricted credits out of the regular pool so they get their own rows.
  const restrictedCreditIds = useMemo(
    () =>
      new Set(
        eligibleRestrictedPcCredits.map(
          (eligibleCredit) => eligibleCredit.capOneCreditId.value,
        ),
      ),
    [eligibleRestrictedPcCredits],
  );
  const vesperPcCreditCarveOut = Boolean(
    isVesperPcCreditEnabled && eligibleRestrictedPcCredits.length > 0,
  );

  const effectiveCredits = useMemo(() => {
    if (!credits || !vesperPcCreditCarveOut) return credits;
    const totalCarvedOut = eligibleRestrictedPcCredits.reduce(
      (sum, eligibleCredit) =>
        sum + Math.abs(eligibleCredit.usableAmount.amount),
      0,
    );
    // aggregate amount is stored negative; subtract absolute carved value to shrink magnitude
    const carvedAmount = Math.abs(credits.amount.amount) - totalCarvedOut;
    // when all regular credits pool are consumed by the Vesper/restricted credits carveout, means there are no regular credits left
    if (carvedAmount <= 0) return undefined;
    return {
      ...credits,
      amount: {
        ...credits.amount,
        amount: carvedAmount * -1,
      },
      breakdown: credits.breakdown?.filter(
        (breakdownEntry) =>
          !restrictedCreditIds.has(
            (breakdownEntry as StatementCreditDetail).capOneCreditId?.value,
          ),
      ),
    };
  }, [
    credits,
    vesperPcCreditCarveOut,
    eligibleRestrictedPcCredits,
    restrictedCreditIds,
  ]);

  const effectiveCreditBreakdown = useMemo(
    () =>
      vesperPcCreditCarveOut
        ? creditBreakdown?.filter(
            (breakdownEntry) =>
              !restrictedCreditIds.has(
                (breakdownEntry as StatementCreditDetail).capOneCreditId?.value,
              ),
          )
        : creditBreakdown,
    [creditBreakdown, vesperPcCreditCarveOut, restrictedCreditIds],
  );

  const displayedRef = useRef(false);
  useEffect(() => {
    if (
      displayedRef.current ||
      !isVesperPcCreditEnabled ||
      eligibleRestrictedPcCredits.length === 0
    )
      return;
    displayedRef.current = true;
    eligibleRestrictedPcCredits.forEach((restrictedCredit) => {
      trackEvent({
        eventName: RESTRICTED_TRAVEL_CREDIT_DISPLAYED,
        properties: {
          credit_id: restrictedCredit.capOneCreditId.value,
          credit_product_name: restrictedCredit.productType,
          credit_restrictions: stringifyCreditRestrictions(
            restrictedCredit.restrictions,
          ),
          lodging_id: selectedLodgingId,
        },
      });
    });
  }, [eligibleRestrictedPcCredits]);

  const autoAppliedRef = useRef(false);
  useEffect(() => {
    if (autoAppliedRef.current) return;
    if (isVesperPcCreditEnabled && eligibleRestrictedPcCredits.length > 0) {
      const hasAmounts = maxApplicableRestrictedCreditAmounts.some(
        ({ amount }) => amount !== 0,
      );
      if (!hasAmounts) return; // price quote not ready yet - wait for next render
      autoAppliedRef.current = true;
      maxApplicableRestrictedCreditAmounts.forEach(
        ({ capOneCreditId, amount }) => {
          if (amount !== 0)
            setRestrictedCreditAmountToApply({ capOneCreditId, amount });
        },
      );
    } else if (effectiveCredits) {
      autoAppliedRef.current = true;
      setCreditToApply(effectiveCredits);
    }
  }, [effectiveCredits, maxApplicableRestrictedCreditAmounts]);

  useEffect(() => {
    if (restrictedCreditCoversTotal && creditsToApply) {
      setCreditToApply(undefined);
    }
  }, [restrictedCreditCoversTotal]);

  useEffect(() => {
    const hasRealRewardsSelection =
      selectedRewardsPaymentAccountId &&
      selectedRewardsPaymentAccountId !== DO_NOT_APPLY_REWARDS_KEY;

    if (selectedPaymentMethodId || hasRealRewardsSelection) {
      if (!autoSelectExperimentOn) {
        resetPaymentCardSelectedAccounts();
      }
      setSelectedRewardsPaymentTotal("", null, null, null);
    }
  }, [
    offerToApply,
    creditsToApply,
    autoSelectExperimentOn,
    appliedRestrictedCredits,
  ]);

  useEffect(() => {
    if (filteredBestOverallOffer) {
      setOfferToApply(filteredBestOverallOffer);
    }
  }, [filteredBestOverallOffer]);

  if (
    !filteredOffers &&
    !effectiveCredits &&
    !hasIneligibleFTB &&
    !(isVesperPcCreditEnabled && eligibleRestrictedPcCredits.length > 0)
  )
    return null;

  const restrictedCreditSlot =
    isVesperPcCreditEnabled && eligibleRestrictedPcCredits.length > 0 ? (
      <>
        {eligibleRestrictedPcCredits.map((restrictedCredit) => {
          const creditId = restrictedCredit.capOneCreditId.value;
          const appliedEntry = appliedRestrictedCredits.find(
            (appliedCredit) => appliedCredit.capOneCreditId === creditId,
          );
          const appliedAmount = appliedEntry?.amount ?? 0;
          const maxEntry = maxApplicableRestrictedCreditAmounts.find(
            (maxCredit) => maxCredit.capOneCreditId === creditId,
          );
          const maxAmount = maxEntry?.amount ?? 0;
          const creditToApply: TravelWalletCredit | undefined =
            appliedAmount !== 0
              ? {
                  id: creditId,
                  amount: {
                    amount: appliedAmount,
                    currency: restrictedCredit.usableAmount.currency,
                  },
                }
              : undefined;
          const isThisSelected = Math.abs(appliedAmount) > 0;
          return (
            <TravelWalletRestrictedCreditSelection
              key={creditId}
              restrictedCredit={restrictedCredit}
              creditToApply={creditToApply}
              setCreditToApply={(newCreditToApply?: TravelWalletCredit) => {
                const prev = creditToApply?.amount.amount ?? 0;
                const next = newCreditToApply
                  ? newCreditToApply.amount.amount
                  : 0;
                setRestrictedCreditAmountToApply({
                  capOneCreditId: creditId,
                  amount: next,
                });
                const sharedProps = {
                  credit_id: creditId,
                  credit_product_name: restrictedCredit.productType,
                  credit_restrictions: stringifyCreditRestrictions(
                    restrictedCredit.restrictions,
                  ),
                  lodging_id: selectedLodgingId,
                };
                if (prev === 0 && next !== 0) {
                  trackEvent({
                    eventName: RESTRICTED_TRAVEL_CREDIT_TOGGLED,
                    properties: {
                      ...sharedProps,
                      selected: true,
                      applied_amount: Math.abs(next),
                    },
                  });
                } else if (prev !== 0 && next === 0) {
                  trackEvent({
                    eventName: RESTRICTED_TRAVEL_CREDIT_TOGGLED,
                    properties: { ...sharedProps, selected: false },
                  });
                } else if (prev !== 0 && next !== 0 && prev !== next) {
                  trackEvent({
                    eventName: EDITED_RESTRICTED_CREDIT_AMOUNT,
                    properties: {
                      ...sharedProps,
                      applied_amount: Math.abs(next),
                      previous_amount: Math.abs(prev),
                    },
                  });
                }
              }}
              maxApplicableAmount={maxAmount}
              isTravelWalletOfferPaymentOnly={isTravelWalletOfferPaymentOnly}
              disabled={disabled}
              isMobile={isMobile}
              getCreditDescription={getSelectedCreditDescription}
              getOfferDescription={getSelectedOfferDescription}
              coversTotal={restrictedCreditCoversTotal && !offerToApply}
              disableChoice={isBookingTotalCovered && !isThisSelected}
              creditLabel={APPLY_VESPER_CREDIT_LABEL}
            />
          );
        })}
      </>
    ) : undefined;

  const tenant = getConfigTenant();
  const ResolvedCreditActivityDialog = getCreditActivityDialogByTenant(tenant);
  const isCapone = !isCorpTenant(tenant) && !isVesperTenant(tenant);

  const creditActivityDialog = creditsHistoryTransaction?.length ? (
    <ResolvedCreditActivityDialog
      trigger={<ActionLink content={VIEW_CREDIT_HISTORY_CTA} />}
      groups={getCreditActivityGroups(
        creditsHistoryTransaction,
        effectiveCreditBreakdown,
      )}
      upcomingItems={getUpcomingCreditActivityItems(effectiveCreditBreakdown)}
      partialHistoryValidFromDate={formatPartialHistoryValidFromDate(
        partialHistoryValidFromTime,
      )}
    />
  ) : undefined;

  return (
    <TravelWalletSelectionWorkflow
      isMobile={isMobile}
      offersAvailable={filteredOffers || []}
      creditsAvailable={effectiveCredits}
      offerToApply={offerToApply}
      creditsToApply={creditsToApply}
      setOfferToApply={setOfferToApply}
      setCreditToApply={setCreditToApply}
      titles={{
        creditTitle: APPLY_TRAVEL_CREDIT_TITLE,
        creditSubtitle: TRAVEL_CREDIT_SUBTITLE,
        offersTitle: APPLY_TRAVEL_OFFER_TITLE,
        offersSubtitle: TRAVEL_OFFER_SUBTITLE,
      }}
      getCreditDescription={getSelectedCreditDescription}
      getOfferDescription={getSelectedOfferDescription}
      disabled={disabled}
      totalCoverageTexts={{
        creditsCoverTotalText: isTravelCreditPaymentOnly
          ? TRAVEL_CREDIT_TOTAL_PAYMENT_TEXT
          : undefined,
        offerCoversTotalText: isTravelWalletOfferPaymentOnly
          ? TRAVEL_OFFER_TOTAL_PAYMENT_TEXT
          : undefined,
        travelWalletCoverTotalText: isStackedTravelWalletPaymentOnly
          ? TRAVEL_CREDIT_AND_OFFER_TOTAL_PAYMENT_TEXT
          : undefined,
      }}
      isTravelWalletCreditPaymentOnly={isTravelCreditPaymentOnly}
      isTravelWalletOfferPaymentOnly={isTravelWalletOfferPaymentOnly}
      maxApplicableCreditAmount={maxApplicableTravelWalletCredit}
      travelCreditHistoryProps={
        creditsHistoryTransaction?.length
          ? {
              modalContentProps: {
                header: CREDIT_BALANCE_DESCRIPTION(firstName),
                balanceText: CREDIT_HISTORY_BALANCE_TEXT(
                  CurrencyFormatters.getSymbol(
                    effectiveCredits?.amount.currency,
                  ),
                  CurrencyFormatters.get(
                    effectiveCredits?.amount.currency,
                  ).format(
                    (effectiveCredits?.amount?.amount &&
                      effectiveCredits?.amount?.amount * -1) ||
                      0,
                  ),
                ),
                subHeader: CREDIT_HISTORY_MODAL_SUBHEADER,
                creditListHeader: CREDIT_HISTORY_MODAL_LIST_HEADER,
              },
              viewHistoryCTAText: VIEW_CREDIT_HISTORY_CTA,
              creditTransactionsHistory: creditsHistoryTransaction,
              credit: effectiveCredits,
              creditBreakdown: effectiveCreditBreakdown,
              rewardsAccounts,
            }
          : undefined
      }
      creditActivityDialogSlot={
        creditActivityDialog && isCapone ? (
          <CaponeEmbeddedProvider>{creditActivityDialog}</CaponeEmbeddedProvider>
        ) : (
          creditActivityDialog
        )
      }
      isAnnualTravelCreditsExperiment={isAnnualTravelCreditsExperiment}
      isCreditAndOfferStackingExperimentV1={
        isCreditAndOfferStackingExperimentV1
      }
      restrictedATCText={
        getRestrictedATCText(rewardsAccounts, effectiveCreditBreakdown) ||
        getRestrictedUserTCTexts(rewardsAccounts, effectiveCreditBreakdown)?.[0]
      }
      isTravelSale={isTravelSale}
      onClickOfferTooltip={(offer) => {
        trackEvent({
          eventName: ENGAGED_OFFER_CTA,
          properties: {
            location: SelectedTravelOfferScreen.PREMIUM_STAYS_CHECKOUT,
            entry_type: "tooltip",
            funnel: offer.funnels.join(","),
          },
          encryptedProperties: [
            offer.trackingPropertiesV2?.encryptedProperties ?? "",
          ],
        });
      }}
      ineligibleCreditsBreakdown={ineligibleCreditsBreakdown}
      bookingTotal={bookingTotal}
      allWalletOffers={allWalletOffers}
      currentFunnel={currentFunnel}
      restrictedCreditSlot={restrictedCreditSlot}
      disableCreditChoice={isBookingTotalCovered && !creditsToApply}
    />
  );
};
